import React, { useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Dialog, Box, Typography, Slide } from '@mui/material';
import { Print as PrintIcon, Article as ArticleIcon } from '@mui/icons-material';
import PatientCodeBarcodes from 'components/PatientCodeBarcodes';
import { resolveCoupleSpouseCodes } from 'utils/printBarcodes';

// Slide-in-from-right transition used when the report is opened full screen
const SlideLeftTransition = React.forwardRef(function SlideLeftTransition(props, ref) {
  return <Slide direction="left" ref={ref} {...props} />;
});

const PRINT_STYLES = `
  @page { margin: 8mm; size: A4 landscape; }
  @media print {
    body { margin: 0; padding: 0; }
    .cs-report-container { margin: 0; padding: 0; background: #F5E6D3; }
    .no-print { display: none !important; }
  }
`;

const CREAM_BG = '#F5E6D3';
const BORDER_COLOR = '#8B7355';

export default function FolliculometryComparisonSheet({
  open,
  onClose,
  days = [],
  patient = {},
  procedure = {},
  folliculometry = {},
  fullScreen = false,
}) {
  const printRef = useRef(null);

  // Extract contact information from procedure or folliculometry
  const contactInfo = useMemo(() => {
    // Try to get contact directly from folliculometry (from enriched API response)
    if (folliculometry?.irm_contacts) {
      const contact = folliculometry.irm_contacts;
      console.log('[CS Report] Using contact from folliculometry.irm_contacts:', contact);
      return {
        name: contact.full_name || contact.first_name || contact.name,
        externalCode: contact.external_code || contact.mrn,
        mrn: contact.mrn,
      };
    }

    // Try to get contact from procedure first
    if (procedure?.irm_contacts) {
      const contact = procedure.irm_contacts;
      console.log('[CS Report] Using contact from procedure.irm_contacts:', contact);
      return {
        name: contact.full_name || contact.first_name || contact.name,
        externalCode: contact.external_code || contact.mrn,
        mrn: contact.mrn,
      };
    }
    
    // Try to get from folliculometry procedure relationship
    if (folliculometry?.irm_procedures?.irm_contacts) {
      const contact = folliculometry.irm_procedures.irm_contacts;
      console.log('[CS Report] Using contact from folliculometry.irm_procedures.irm_contacts:', contact);
      return {
        name: contact.full_name || contact.first_name || contact.name,
        externalCode: contact.external_code || contact.mrn,
        mrn: contact.mrn,
      };
    }

    // Fallback to patient object
    console.log('[CS Report] Fallback to patient object:', patient);
    return {
      name: patient?.full_name || patient?.first_name || patient?.name,
      externalCode: patient?.external_code || patient?.mrn,
      mrn: patient?.mrn,
    };
  }, [procedure, folliculometry, patient]);

  // Merge patient data with contact info
  const displayPatient = useMemo(() => ({
    full_name: contactInfo.name || patient?.full_name || patient?.first_name || patient?.name,
    mrn: contactInfo.mrn || patient?.mrn,
    external_code: contactInfo.externalCode || patient?.external_code,
    registration_no: patient?.registration_no,
    id: patient?.id,
  }), [contactInfo, patient]);

  // Same candidate objects contactInfo tries, in order, for couple/spouse codes.
  const { coupleCode, spouseCode } = useMemo(() => {
    const candidate = folliculometry?.irm_contacts || procedure?.irm_contacts
      || folliculometry?.irm_procedures?.irm_contacts || patient;
    return resolveCoupleSpouseCodes(candidate);
  }, [folliculometry, procedure, patient]);

  const handlePrint = () => {
    const styleEl = document.createElement('style');
    styleEl.id = '__cs_report_print_styles__';
    styleEl.innerHTML = PRINT_STYLES;
    document.head.appendChild(styleEl);
    
    window.print();
    
    setTimeout(() => {
      const el = document.getElementById('__cs_report_print_styles__');
      if (el) el.remove();
    }, 1000);
  };

  if (!open) return null;

  // Sort days
  let sortedDays = [...(days || [])].sort((a, b) => {
    if (a.cycle_day && b.cycle_day) return Number(a.cycle_day) - Number(b.cycle_day);
    if (a.date && b.date) return new Date(a.date) - new Date(b.date);
    return 0;
  });

  // Ensure minimum 5 days are displayed
  const MIN_DAYS_TO_DISPLAY = 5;
  if (sortedDays.length < MIN_DAYS_TO_DISPLAY) {
    const emptyDaysNeeded = MIN_DAYS_TO_DISPLAY - sortedDays.length;
    const emptyDays = Array.from({ length: emptyDaysNeeded }, (_, i) => ({
      id: `empty_${sortedDays.length + i}`,
      isEmptyPlaceholder: true,
      records: [],
      params: [],
    }));
    sortedDays = [...sortedDays, ...emptyDays];
  }

  // Extract unique follicle sizes
  const uniqueSizes = new Set();
  sortedDays.forEach(day => {
    if (day.records && Array.isArray(day.records)) {
      day.records.forEach(rec => {
        if (rec.follicle_size) uniqueSizes.add(rec.follicle_size);
      });
    }
  });
  const sortedSizes = Array.from(uniqueSizes).sort((a, b) => {
    if (a === '<10' || b === '>21') return -1;
    if (a === '>21' || b === '<10') return 1;
    return parseFloat(a) - parseFloat(b);
  });

  const getDayValue = (day, side, size) => {
    if (!day.records || !Array.isArray(day.records)) return 0;
    const rec = day.records.find(r => r.side === side && r.follicle_size === size);
    return rec?.count || 0;
  };

  const getDayParams = (day, paramName) => {
    if (!day.params || !Array.isArray(day.params)) return '-';
    
    // First try to find by param_group_member?.name (old structure)
    const param = day.params.find(p => p.param_group_member?.name === paramName);
    if (param) {
      return param.value || param.param_group_member_option?.option_value || '-';
    }
    
    // Try to find by param_type mapping to lookup names
    // Map lookup names to param_type values
    const typeMap = {
      'DRUGS': 'DRUGS',
      'TRIGGER': 'TRIGGER',
      'SERM': 'SERM',
    };
    
    const paramType = typeMap[paramName];
    if (paramType) {
      const typeParams = day.params.filter(p => p.param_type === paramType);
      if (typeParams.length > 0) {
        const values = typeParams
          .map(p => p.param_value || p.param_name)
          .filter(v => v && String(v).trim());
        return values.length > 0 ? values.join(', ') : '-';
      }
    }
    
    return '-';
  };

  // Generic formatter for params with name [value] format
  const getFormattedParams = (day, paramType) => {
    if (!day.params || !Array.isArray(day.params)) return '-';
    
    const params = day.params.filter(p => {
      // Check param_type field (new structure)
      if (p.param_type === paramType) return true;
      
      // Check param_group_member?.name field (old/backend structure)
      const name = (p.param_group_member?.name || '').toUpperCase();
      return name.toUpperCase() === paramType;
    });
    
    if (params.length === 0) return '-';
    
    // Extract name and value pairs and format them
    const formatted = params
      .map(p => {
        // Try new structure first (param_name and param_value)
        if (p.param_name || p.param_value) {
          const name = (p.param_name || '').trim();
          const value = (p.param_value || '').trim();
          if (name && value) return `${name} [${value}]`;
          if (value) return value;
          if (name) return name;
          return null;
        }
        // Fallback to old structure - try to get option_value
        const name = (p.param_group_member?.name || '').trim();
        const value = (p.value || p.param_group_member_option?.option_value || '').trim();
        if (name && value) return `${name} [${value}]`;
        if (value) return value;
        return null;
      })
      .filter(v => v && String(v).trim());
    
    return formatted.length > 0 ? formatted.join(', ') : '-';
  };

  // Extract all matching SERM values with param names (e.g., "E2 [123], PRL [45]")
  const getAllSermValues = (day) => getFormattedParams(day, 'SERM');

  // Format one structured drug entry (drug_type/drug_name/dose/route/days/times)
  const formatDrugEntry = (d) => {
    const parts = [];
    if (d.dose) parts.push(d.dose);
    if (d.route) parts.push(d.route);
    if (d.days) parts.push(`${d.days}d`);
    if (Array.isArray(d.times) && d.times.length > 0) parts.push(d.times.join('/'));
    const detail = parts.join(', ');
    return detail ? `${d.drug_name} [${detail}]` : d.drug_name;
  };

  // Extract all Drugs for a day — prefers the structured drugs table; falls back to the
  // legacy generic params (param_type === 'DRUGS') for days saved before the Drugs revamp.
  const getAllDrugsValues = (day) => {
    if (Array.isArray(day.drugs) && day.drugs.length > 0) {
      const formatted = day.drugs.map(formatDrugEntry).filter(v => v && String(v).trim());
      if (formatted.length > 0) return formatted.join(', ');
    }
    return getFormattedParams(day, 'DRUGS');
  };

  // Extract all matching TRIGGER values
  const getAllTriggerValues = (day) => getFormattedParams(day, 'TRIGGER');

  // Get cycle type
  const getCycleType = (day) => {
    if (!day.params || !Array.isArray(day.params)) return '-';
    
    const cycleParam = day.params.find(p => {
      if (p.param_type === 'CYCLE_TYPE') return true;
      const name = (p.param_group_member?.name || '').toUpperCase();
      return name === 'CYCLE_TYPE';
    });
    
    if (!cycleParam) return '-';
    
    // Try new structure first
    if (cycleParam.param_value) return cycleParam.param_value;
    if (cycleParam.value) return cycleParam.value;
    if (cycleParam.param_group_member_option?.option_value) return cycleParam.param_group_member_option.option_value;
    
    return '-';
  };

  const colWidth = 100 / (sortedDays.length * 2 + 1);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={fullScreen ? false : 'lg'}
      fullWidth={!fullScreen}
      fullScreen={fullScreen}
      TransitionComponent={fullScreen ? SlideLeftTransition : undefined}
      PaperProps={{ sx: fullScreen ? { borderRadius: 0 } : { borderRadius: '12px' } }}
    >
      {/* Dialog Header */}
      <Box sx={{ background: 'linear-gradient(135deg, #00798E 0%, #005f75 100%)', px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'flex-start', flexShrink: 0 }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <ArticleIcon sx={{ color: 'white', fontSize: 22 }} />
          <Typography sx={{ fontWeight: 700, color: 'white', fontSize: '0.95rem' }}>
            CS Report - Comparison Sheet
          </Typography>
        </Box>
      </Box>

      {/* Dialog Content */}
      <Box sx={fullScreen ? { p: 2, flex: 1, overflowY: 'auto' } : { p: 2, maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
        {/* Buttons */}
        <Box display="flex" justifyContent="flex-end" gap={1} mb={2} className="no-print">
          <button
            type="button"
            onClick={handlePrint}
            style={{
              padding: '10px 24px',
              background: '#00798E',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              transition: 'background 0.3s ease',
            }}
            onMouseOver={(e) => (e.currentTarget.style.background = '#006379')}
            onMouseOut={(e) => (e.currentTarget.style.background = '#00798E')}
          >
            <PrintIcon fontSize="small" />
            Print Report
          </button>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '10px 24px',
              background: '#e0e0e0',
              color: '#333',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'background 0.3s ease',
            }}
            onMouseOver={(e) => (e.currentTarget.style.background = '#bdbdbd')}
            onMouseOut={(e) => (e.currentTarget.style.background = '#e0e0e0')}
          >
            Close
          </button>
        </Box>

        {/* Printable Content */}
        <Box ref={printRef} className="cs-report-container" sx={{ backgroundColor: CREAM_BG, p: 2, border: `2px solid ${BORDER_COLOR}`, fontFamily: 'Arial, sans-serif', fontSize: '0.75rem' }}>
          {/* Header Section */}
          <Box sx={{ mb: 2, pb: 2, borderBottom: `3px solid ${BORDER_COLOR}` }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', mb: 1.5, color: '#00798E' }}>
              IVF Folliculometry - Comparison Sheet
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 1.5 }}>
              <Box>
                <Typography sx={{ fontSize: '0.65rem', color: '#666', fontWeight: 600, display: 'inline', mr: 0.5 }}>
                  PATIENT NAME:
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#000', fontWeight: 700, letterSpacing: '0.5px', display: 'inline' }}>
                  {(displayPatient?.full_name || 'NOT PROVIDED').toUpperCase()}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.65rem', color: '#666', fontWeight: 600, display: 'inline', mr: 0.5 }}>
                  PATIENT ID:
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#000', fontWeight: 700, letterSpacing: '0.5px', fontFamily: 'monospace', display: 'inline' }}>
                  {(displayPatient?.mrn || displayPatient?.external_code || displayPatient?.registration_no || displayPatient?.id || 'NOT PROVIDED').toString()}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.65rem', color: '#666', fontWeight: 600, display: 'inline', mr: 0.5 }}>
                  SESSION ID:
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#000', fontWeight: 700, letterSpacing: '0.5px', display: 'inline' }}>
                  {folliculometry?.id || 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.65rem', color: '#666', fontWeight: 600, display: 'inline', mr: 0.5 }}>
                  REPORT DATE:
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#000', fontWeight: 700, display: 'inline' }}>
                  {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1 }}>
              <PatientCodeBarcodes
                patientCode={displayPatient?.external_code}
                coupleCode={coupleCode}
                spouseCode={spouseCode}
                documentCode={procedure?.external_code}
                documentLabel="Procedure No"
              />
            </Box>
          </Box>

          {/* Main Table */}
          <table style={{ width: '100%', borderCollapse: 'collapse', border: `1px solid ${BORDER_COLOR}`, tableLayout: 'fixed' }}>
            <tbody>
              {/* Date Row */}
              <tr>
                <td style={{ border: `1px solid ${BORDER_COLOR}`, padding: '6px', fontWeight: 700, backgroundColor: '#EDD9C4', fontSize: '0.7rem', width: '12%', minWidth: '80px' }}>
                  Date
                </td>
                {sortedDays.map((day, idx) => {
                  const dayLabel = day.date ? new Date(day.date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' }) : `D${idx}`;
                  return (
                    <td key={day.id || idx} colSpan="2" style={{ border: `1px solid ${BORDER_COLOR}`, padding: '6px', textAlign: 'center', fontWeight: 600, fontSize: '0.7rem', backgroundColor: '#EDD9C4' }}>
                      {dayLabel}
                    </td>
                  );
                })}
              </tr>

              {/* Cycle Day Row */}
              <tr>
                <td style={{ border: `1px solid ${BORDER_COLOR}`, padding: '6px', fontWeight: 700, backgroundColor: '#EDD9C4', fontSize: '0.7rem', width: '12%', minWidth: '80px' }}>
                  Cycle day
                </td>
                {sortedDays.map((day, idx) => (
                  <td key={day.id || idx} colSpan="2" style={{ border: `1px solid ${BORDER_COLOR}`, padding: '6px', textAlign: 'center', fontSize: '0.7rem' }}>
                    {day.cycle_day || '-'}
                  </td>
                ))}
              </tr>

              {/* Endometrium Row */}
              <tr>
                <td style={{ border: `1px solid ${BORDER_COLOR}`, padding: '6px', fontWeight: 700, backgroundColor: '#EDD9C4', fontSize: '0.7rem', width: '12%', minWidth: '80px' }}>
                  Endometrium
                </td>
                {sortedDays.map((day, idx) => (
                  <td key={day.id || idx} colSpan="2" style={{ border: `1px solid ${BORDER_COLOR}`, padding: '6px', textAlign: 'center', fontSize: '0.7rem' }}>
                    {day.endometrium || '-'}
                  </td>
                ))}
              </tr>

              {/* Follicle Header Row */}
              <tr style={{ backgroundColor: '#E8D4C0' }}>
                <td style={{ border: `1px solid ${BORDER_COLOR}`, padding: '6px', fontWeight: 700, fontSize: '0.7rem', width: '12%', minWidth: '80px' }}>
                  Follicle
                </td>
                {sortedDays.map((day, idx) => (
                  <React.Fragment key={day.id || idx}>
                    <td style={{ border: `1px solid ${BORDER_COLOR}`, padding: '4px', textAlign: 'center', fontWeight: 700, fontSize: '0.65rem' }}>R</td>
                    <td style={{ border: `1px solid ${BORDER_COLOR}`, padding: '4px', textAlign: 'center', fontWeight: 700, fontSize: '0.65rem' }}>L</td>
                  </React.Fragment>
                ))}
              </tr>

              {/* Follicle Size Rows */}
              {sortedSizes.map((size, sizeIdx) => (
                <tr key={size} style={{ backgroundColor: sizeIdx % 2 === 0 ? 'white' : '#FDFBF8' }}>
                  <td style={{ border: `1px solid ${BORDER_COLOR}`, padding: '5px', fontWeight: 600, fontSize: '0.7rem', textAlign: 'center', width: '12%', minWidth: '80px' }}>
                    {size}
                  </td>
                  {sortedDays.map((day, dayIdx) => {
                    const rightCount = getDayValue(day, 'right', size);
                    const leftCount = getDayValue(day, 'left', size);
                    return (
                      <React.Fragment key={dayIdx}>
                        <td style={{ border: `1px solid ${BORDER_COLOR}`, padding: '5px', textAlign: 'center', fontSize: '0.7rem', fontWeight: rightCount > 0 ? 700 : 400 }}>
                          {rightCount || ''}
                        </td>
                        <td style={{ border: `1px solid ${BORDER_COLOR}`, padding: '5px', textAlign: 'center', fontSize: '0.7rem', fontWeight: leftCount > 0 ? 700 : 400 }}>
                          {leftCount || ''}
                        </td>
                      </React.Fragment>
                    );
                  })}
                </tr>
              ))}

              {/* Cycle Type Row */}
              <tr>
                <td style={{ border: `1px solid ${BORDER_COLOR}`, padding: '5px', fontWeight: 600, fontSize: '0.65rem', width: '12%', minWidth: '80px' }}>
                  Cycle Type
                </td>
                {sortedDays.map((day, idx) => (
                  <td key={day.id || idx} colSpan="2" style={{ border: `1px solid ${BORDER_COLOR}`, padding: '5px', textAlign: 'center', fontSize: '0.65rem', wordWrap: 'break-word', lineHeight: '1.3' }}>
                    {getCycleType(day)}
                  </td>
                ))}
              </tr>

              {/* SERM Row */}
              <tr>
                <td style={{ border: `1px solid ${BORDER_COLOR}`, padding: '5px', fontWeight: 600, fontSize: '0.65rem', width: '12%', minWidth: '80px' }}>
                  SERUM
                </td>
                {sortedDays.map((day, idx) => (
                  <td key={day.id || idx} colSpan="2" style={{ border: `1px solid ${BORDER_COLOR}`, padding: '5px', textAlign: 'center', fontSize: '0.65rem', wordWrap: 'break-word', lineHeight: '1.3' }}>
                    {getAllSermValues(day)}
                  </td>
                ))}
              </tr>

              {/* Drugs Row */}
              <tr>
                <td style={{ border: `1px solid ${BORDER_COLOR}`, padding: '5px', fontWeight: 600, fontSize: '0.65rem', width: '12%', minWidth: '80px' }}>
                  Drugs
                </td>
                {sortedDays.map((day, idx) => (
                  <td key={day.id || idx} colSpan="2" style={{ border: `1px solid ${BORDER_COLOR}`, padding: '5px', textAlign: 'center', fontSize: '0.65rem', wordWrap: 'break-word', lineHeight: '1.3' }}>
                    {getAllDrugsValues(day)}
                  </td>
                ))}
              </tr>

              {/* Trigger / GnRH Row */}
              <tr>
                <td style={{ border: `1px solid ${BORDER_COLOR}`, padding: '5px', fontWeight: 600, fontSize: '0.65rem', width: '12%', minWidth: '80px' }}>
                  Triggers
                </td>
                {sortedDays.map((day, idx) => (
                  <td key={day.id || idx} colSpan="2" style={{ border: `1px solid ${BORDER_COLOR}`, padding: '5px', textAlign: 'center', fontSize: '0.65rem', wordWrap: 'break-word', lineHeight: '1.3' }}>
                    {getAllTriggerValues(day)}
                  </td>
                ))}
              </tr>

              {/* Action Row */}
              <tr>
                <td style={{ border: `1px solid ${BORDER_COLOR}`, padding: '5px', fontWeight: 600, fontSize: '0.65rem', width: '12%', minWidth: '80px' }}>
                  Action
                </td>
                {sortedDays.map((day, idx) => (
                  <td key={day.id || idx} colSpan="2" style={{ border: `1px solid ${BORDER_COLOR}`, padding: '5px', fontSize: '0.65rem', wordWrap: 'break-word', lineHeight: '1.3' }}>
                    {day.actions || '-'}
                  </td>
                ))}
              </tr>

              {/* Comments Row */}
              <tr>
                <td style={{ border: `1px solid ${BORDER_COLOR}`, padding: '5px', fontWeight: 600, fontSize: '0.65rem', width: '12%', minWidth: '80px' }}>
                  Comments
                </td>
                {sortedDays.map((day, idx) => (
                  <td key={day.id || idx} colSpan="2" style={{ border: `1px solid ${BORDER_COLOR}`, padding: '5px', fontSize: '0.65rem', wordWrap: 'break-word', lineHeight: '1.3' }}>
                    {day.comments || '-'}
                  </td>
                ))}
              </tr>

              {/* Initials Row */}
              <tr style={{ backgroundColor: '#FDFBF8' }}>
                <td style={{ border: `1px solid ${BORDER_COLOR}`, padding: '6px', fontWeight: 700, fontSize: '0.65rem', width: '12%', minWidth: '80px' }}>Initials</td>
                {sortedDays.map((day, idx) => (
                  <td key={day.id || idx} colSpan="2" style={{ border: `1px solid ${BORDER_COLOR}`, padding: '8px', textAlign: 'center', minHeight: '20px' }}>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>

          {/* Footer */}
          <Box sx={{ mt: 1.5, pt: 1, borderTop: `1px solid ${BORDER_COLOR}`, fontSize: '0.65rem', color: '#666' }}>
            <Typography sx={{ fontSize: '0.65rem' }}>
              Generated: {new Date().toLocaleString('en-GB')}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}

FolliculometryComparisonSheet.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  days: PropTypes.array,
  patient: PropTypes.object,
  procedure: PropTypes.object,
  folliculometry: PropTypes.object,
  fullScreen: PropTypes.bool,
};

FolliculometryComparisonSheet.defaultProps = {
  open: false,
  onClose: () => {},
  days: [],
  patient: {},
  procedure: {},
  folliculometry: {},
  fullScreen: false,
};
